import type { CountryFeedDiff } from "@/interfaces/campaignDraft";
import type { Product } from "@/interfaces/product";
import type { CollectionWithProducts } from "@/types/types";
import { shipsToCountry } from "./redemption-helper";

const CHARITY_IMPACT_PREFIX = /^charity impact:\s*/i;

/** Display-only: the stored Shopify name is untouched. Twin of TITLE_PREFIX in the API's charityCauses.ts. */
export function displayProductName(name: string): string {
  return name.replace(CHARITY_IMPACT_PREFIX, "");
}

export function isDigitalProduct(product: Pick<Product, "category">): boolean {
  const category = product.category as string;
  return category === "Donation" || category === "Experience";
}

export function orderCollectionProducts(collections: CollectionWithProducts[]): Product[] {
  const base = collections.filter((c) => c.role === "base");
  const expansions = collections.filter((c) => c.role === "expansion");
  const physicalBlock = base.flatMap((c) => c.products.filter((p) => !isDigitalProduct(p)));
  const digitalBlock = base.flatMap((c) => c.products.filter((p) => isDigitalProduct(p)));
  const expansionProducts = expansions.flatMap((c) => c.products);
  return [...physicalBlock, ...digitalBlock, ...expansionProducts];
}

/**
 * Compose a budget's collections into the ordered grid for a country: flatten ALL of the tier's
 * collections, then keep only products shippable to the country (`shipsToCountry`). Country-locking
 * is a PER-PRODUCT property (`shippableCountries`), not a collection-type one — a physical gift shows
 * only when its list includes the country (domestic empties are stamped ["US"] server-side; an empty
 * list here = no Shopify data → international, hidden until re-sync) while charity/experience/digital
 * gifts are country-agnostic and always show. Filtering here at the product level (rather than dropping
 * whole non-domestic collections for US) is what lets the charity-only tiers ($5/$10) render on US.
 * The raw per-country grid, before any sender edits.
 */
export function composeForCountry(
  collections: CollectionWithProducts[],
  country: string,
): Product[] {
  const ordered = orderCollectionProducts(collections);
  return ordered.filter((p) => shipsToCountry(p, country));
}

/**
 * Resolve a country's feed = compose(country) + that country's edits (remove excluded, merge in
 * added-from-lower-tier products, apply the order override with the rest appended). No edits ⇒
 * raw compose. SHARED by the builder (per-country grid) and the recipient flow so they can't
 * drift — mirrors the server's per-country resolution, on Product objects.
 */
export function resolveCountryFeed(
  collections: CollectionWithProducts[],
  country: string,
  diff: CountryFeedDiff | undefined,
  addedProducts: Product[],
): Product[] {
  const composed = composeForCountry(collections, country);
  if (!diff) return composed;

  const excluded = new Set(diff.excluded);
  const base = composed.filter((p) => !excluded.has(p.id));
  const baseIds = new Set(base.map((p) => p.id));
  const added = diff.added
    .map((id) => addedProducts.find((p) => p.id === id))
    .filter((p): p is Product => !!p && !baseIds.has(p.id));
  const membership = [...base, ...added];

  if (diff.order.length === 0) return membership;
  const idx = new Map(diff.order.map((id, i) => [id, i] as const));
  const ordered = membership
    .filter((p) => idx.has(p.id))
    .sort((a, b) => (idx.get(a.id) ?? 0) - (idx.get(b.id) ?? 0));
  const rest = membership.filter((p) => !idx.has(p.id));
  return [...ordered, ...rest];
}

/**
 * Ids of builder Step 1 cards that came from a lower tier via Add More (badge "Added from lower
 * price tier"): grid products absent from the tier's own list. `tierBaseline` is the tier's raw
 * composed list for the country (`composeForCountry`); empty ⇒ it is still loading, so nothing is badged.
 */
export function addedFromLowerTierIds(grid: Product[], tierBaseline: Product[]): Set<string> {
  if (tierBaseline.length === 0) return new Set();
  const baselineIds = new Set(tierBaseline.map((p) => p.id));
  return new Set(grid.filter((p) => !baselineIds.has(p.id)).map((p) => p.id));
}
